import React, { useCallback, useMemo } from "react";
import "./FacesheetHistory.scss";
import { LoadingBar } from "../LoadingComponents/LoadingBar";
import moment from "moment";
import { sortBy, reverse } from "lodash-es";
import { getNavigationUrlByEncounterId } from "../../services/encounterNavigationService";
import { Link } from "react-router-dom";
import * as fileDownloader from "../../services/fileDownloader";

function PaymentLink({ x }) {
    return (
        <Link to={"/interview/" + x.encounterId + "/details/payment"} className="facesheet-history__link">
            {x.totalPayment !== null && x.totalPayment > 0 ? "Payment made : " + x.totalPayment : "Make Payment"}
        </Link>
    );
}

function NavigationLink({ x, shouldShowUmmcNavigation }) {
    const inProgressStatuses = ["New", "In Progress"];
    return (
        <div>
            {inProgressStatuses.indexOf(x.encounterStatus) >= 0 && (
                <Link
                    to={getNavigationUrlByEncounterId(x.encounterId, x.encounterStatus, true)}
                    className="facesheet-history__link"
                >
                    {"Encounter"}
                </Link>
            )}

            {(x.encounterStatus === "Complete" || x.encounterStatus === "Pending Decision") && (
                <>
                    <Link to={`/interview/${x.encounterId}/details/dispatch`} className="facesheet-history__link">
                        {"Forms & Applications"}
                    </Link>
                    {shouldShowUmmcNavigation &&
                        (x?.selectedProgram?.indexOf("Financial") >= 0 ||
                            x?.selectedProgram?.indexOf("Charity") >= 0) && (
                            <Link
                                to={`/interview/${x.encounterId}/details/UMMCFADocumentation`}
                                className="facesheet-history__link"
                            >
                                {"FA Documentation"}
                            </Link>
                        )}
                </>
            )}
            {x.encounterStatus === "Pending Decision" && (
                <Link to={`/interview/${x.encounterId}/details/approval`} className="facesheet-history__link">
                    {"Program Approval"}
                </Link>
            )}

            {x.encounterStatus === "Pending" && (
                <Link
                    to={getNavigationUrlByEncounterId(x.encounterId, x.encounterStatus, true)}
                    className="facesheet-history__link"
                >
                    {"Marketplace"}
                </Link>
            )}
        </div>
    );
}

export const FacesheetHistory = ({
    encounterHistory,
    patientId,
    patientName = "the patient",
    generatedPdfsForEncounter,
    pdfList,
    faxHistory,
    shouldShowUmmcNavigation,
}) => {
    const sortedEncounters = useMemo(() => {
        return reverse(sortBy(encounterHistory.data, ["encounterDate", "createdDate"]));
    }, [encounterHistory.data]);

    const getServiceDateDisplay = useCallback(x => {
        if (x == null) {
            return "";
        }

        const zeroDate = "01.01.0001";
        if (moment(x.serviceStartDate).format("MM.DD.YYYY") === zeroDate) {
            return moment(x.encounterDate).format("MM.DD.YYYY");
        }

        if (moment(x.serviceEndDate).format("MM.DD.YYYY") === zeroDate) {
            return moment(x.serviceStartDate).format("MM.DD.YYYY");
        }

        return moment(x.serviceStartDate).format("MM.DD.YYYY") + " - " + moment(x.serviceEndDate).format("MM.DD.YYYY");
    }, []);

    const fileDownload = (documentId, fileName) => {
        fileDownloader.downloadFile(`/api/person/${patientId}/document/${documentId}`, {}, false, fileName);
    };
    return (
        <div className="facesheet-history">
            <LoadingBar isLoading={encounterHistory.isInitialized === false || encounterHistory.isLoading === true}>
                <p className="description">Past encounters for {patientName}.</p>
                <table>
                    <thead>
                        <th>Date</th>
                        <th>Status</th>
                        <th>Program</th>
                        <th>User</th>
                        <th>Navigate</th>
                    </thead>
                    <tbody>
                        {sortedEncounters.map(x => {
                            return (
                                <tr className={x.patientId === patientId ? "bold-row" : ""}>
                                    <td>{getServiceDateDisplay(x)}</td>
                                    <td>{x.encounterStatus}</td>
                                    <td>
                                        {x.encounterStatus === "Complete" && (
                                            <>
                                                {x.selectedProgram
                                                    ? x.selectedProgram
                                                    : generatedPdfsForEncounter?.length > 0
                                                    ? "Not-eligible - forms printed"
                                                    : "Not-eligible - forms not printed"}
                                                {x.selectedProgram === "Credit" && <PaymentLink x={x} />}
                                            </>
                                        )}
                                    </td>
                                    <td>
                                        <span className="user"> {x.user}</span>
                                    </td>

                                    <td>
                                        <NavigationLink x={x} shouldShowUmmcNavigation={shouldShowUmmcNavigation} />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                <p className="description">Printed Forms & Applications</p>
                <table>
                    <thead>
                        <th>Name</th>
                        <th>Print Date</th>
                    </thead>
                    <tbody>
                        {generatedPdfsForEncounter?.map(x => (
                            <tr>
                                <td>
                                    <p>
                                        <a
                                            className="facesheet-history__item__line"
                                            href="javascript:void(0)"
                                            onClick={() => {
                                                fileDownload(
                                                    x.documentID,
                                                    pdfList.filter(y => y.key == x.templateKey)[0]?.name
                                                );
                                            }}
                                        >
                                            {pdfList.filter(y => y.key == x.templateKey)[0]?.name}
                                        </a>
                                    </p>
                                </td>
                                <td>
                                    <p className="facesheet-history__item__date">
                                        {moment(x.printDate).format("MM.DD.YYYY")}
                                    </p>
                                </td>
                            </tr>
                        ))}
                        {!generatedPdfsForEncounter ||
                            (generatedPdfsForEncounter?.length == 0 && (
                                <tr className="empty-table">
                                    <td colSpan={2}>
                                        <h6>No forms are printed</h6>
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>

                {faxHistory?.length > 0 && (
                    <>
                        <p className="description">Sent Secure Faxes</p>
                        <table>
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Sent Date</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {faxHistory?.map((x, i) => (
                                    <tr key={i}>
                                        <td>{x.documents.join(", ")}</td>
                                        <td>{moment(x.created).format("MM.DD.YYYY")}</td>
                                        <td>{x.status}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </>
                )}
            </LoadingBar>
        </div>
    );
};
